import React, { useState } from 'react';
import { Button, Card, CardContent, Divider, Grid, Typography } from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { colours } from 'utils/constants';
import { useAuth } from 'services/auth-service';
import AdminService, { AdminProps } from 'services/hooks/admin-service';
import { useQuery } from 'react-query';
import { toast } from 'react-toastify';
import { generateApiFailErrorMessage } from 'utils/helpers';
import LoadingSpinner from 'components/elements/Spinner/LoadingSpinner';
import ResetPasswordForm from './MyProfileForms/ResetPasswordForm';
import ResetEmailForm from './MyProfileForms/ResetEmailForm';

function getFixedLengthMask(length = 10, character = '•') {
  return character.repeat(length);
}

export default function MyProfileView() {
  const {
    state: { user }
  } = useAuth();
  const [editUUID, setEditUUID] = useState('');
  const [view, setView] = useState('profile'); // 'profile', 'resetPassword', or 'resetEmail'

  // Fetch Admin Info
  const { data, isLoading } = useQuery<AdminProps, Error>(
    ['admin', user.sub],
    () => AdminService.findAdminById(user.sub),
    {
      enabled: !!user.sub,
      onError: () => {
        toast.error(generateApiFailErrorMessage('Admin'));
      }
    }
  );

  const handlePasswordReset = () => {
    setEditUUID(user?.sub ? user.sub : '');
    setView('resetPassword');
  };

  const handleEmailReset = () => {
    setEditUUID(user?.sub ? user.sub : '');
    setView('resetEmail');
  };

  const handleBackToProfile = () => {
    setView('profile');
  };

  // Render My Profile Card
  const renderMyProfileCard = (admin: AdminProps) => {
    return (
      <React.Fragment>
        <CardContent>
          {/* Email Address */}
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item xs style={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
              <Typography color="text.primary" sx={{ fontWeight: 'bold' }}>
                Email Address
              </Typography>
            </Grid>
            <Grid
              item
              xs
              style={{
                flexGrow: 2,
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center'
              }}
            >
              <Typography color="text.primary" sx={{ marginRight: 2 }}>
                {admin?.username}
              </Typography>
              <Button
                size="small"
                style={{ textTransform: 'none' }}
                sx={{
                  color: colours.seafoam,
                  fontWeight: 'bold',
                  borderRadius: '50%',
                  minWidth: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center' // Ensures that the icon is vertically centered in the button
                }}
                onClick={handleEmailReset}
              >
                <ArrowForwardIosIcon sx={{ width: '15px' }} />
              </Button>
            </Grid>
          </Grid>
          <Divider variant="middle" style={{ margin: '20px 0' }} />
          {/* Reset Password */}
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item xs style={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
              <Typography color="text.primary" sx={{ fontWeight: 'bold' }}>
                Reset Password
              </Typography>
            </Grid>
            <Grid
              item
              xs
              style={{
                flexGrow: 2,
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center'
              }}
            >
              <Typography color="text.primary" sx={{ marginRight: 2 }}>
                {getFixedLengthMask(15, '•')}
              </Typography>
              <Button
                size="small"
                style={{ textTransform: 'none' }}
                sx={{
                  color: colours.seafoam,
                  fontWeight: 'bold',
                  borderRadius: '50%',
                  minWidth: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center'
                }}
                onClick={handlePasswordReset}
              >
                <ArrowForwardIosIcon sx={{ width: '15px' }} />
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </React.Fragment>
    );
  };

  return !isLoading && data ? (
    <>
      {view === 'profile' ? (
        <div className="space-y-4">
          <Grid
            container
            spacing={4}
            justifyContent="center"
            sx={{
              paddingLeft: {
                xs: '0px !important',
                md: '50px !important',
                lg: '100px !important',
                xl: '200px !important'
              },
              paddingRight: {
                xs: '0px !important',
                md: '50px !important',
                lg: '100px !important',
                xl: '200px !important'
              },
              maxWidth: '100%',
              display: 'flex',
              flexDirection: 'column', // Set flex direction to column
              alignItems: 'center',
              margin: '0 auto',
              '&::after': {
                content: '" "',
                flex: 'auto'
              }
            }}
          >
            <Grid
              item
              xs={12}
              sm={12}
              md={10}
              lg={8}
              sx={{
                paddingLeft: '0px !important',
                width: '100%',
                maxWidth: '1200px',
                mx: 'auto'
              }}
            >
              <h1 className="p-0 mb-10 text-left">My Profile</h1>
              <Card
                variant="outlined"
                sx={{
                  paddingLeft: '10px',
                  paddingRight: '10px',
                  borderRadius: '8px',
                  width: '100%'
                }}
              >
                {renderMyProfileCard(data)}
              </Card>
            </Grid>
          </Grid>
        </div>
      ) : view === 'resetPassword' ? (
        <div className="space-y-4">
          <Grid
            container
            spacing={4}
            justifyContent="center"
            sx={{
              paddingLeft: {
                xs: '0px !important',
                md: '50px !important',
                lg: '100px !important',
                xl: '200px !important'
              },
              paddingRight: {
                xs: '0px !important',
                md: '50px !important',
                lg: '100px !important',
                xl: '200px !important'
              },
              maxWidth: '100%',
              display: 'flex',
              flexDirection: 'column', // Set flex direction to column
              alignItems: 'center',
              margin: '0 auto',
              '&::after': {
                content: '" "',
                flex: 'auto'
              }
            }}
          >
            <Grid
              item
              xs={12}
              sm={12}
              md={10}
              lg={8}
              sx={{
                paddingLeft: '0px !important',
                width: '100%',
                maxWidth: '1200px',
                mx: 'auto'
              }}
            >
              <h1 className="p-0 mb-10 text-left">Reset Password</h1>
              <ResetPasswordForm onBack={handleBackToProfile} editUUID={editUUID} />
            </Grid>
          </Grid>
        </div>
      ) : (
        <div className="space-y-4">
          <Grid
            container
            spacing={4}
            justifyContent="center"
            sx={{
              paddingLeft: {
                xs: '0px !important',
                md: '50px !important',
                lg: '100px !important',
                xl: '200px !important'
              },
              paddingRight: {
                xs: '0px !important',
                md: '50px !important',
                lg: '100px !important',
                xl: '200px !important'
              },
              maxWidth: '100%',
              display: 'flex',
              flexDirection: 'column', // Set flex direction to column
              alignItems: 'center',
              margin: '0 auto',
              '&::after': {
                content: '" "',
                flex: 'auto'
              }
            }}
          >
            <Grid
              item
              xs={12}
              sm={12}
              md={10}
              lg={8}
              sx={{
                paddingLeft: '0px !important',
                width: '100%',
                maxWidth: '1200px',
                mx: 'auto'
              }}
            >
              <h1 className="p-0 mb-10 text-left">Reset Email Address</h1>
              <ResetEmailForm onBack={handleBackToProfile} adminData={data} />
            </Grid>
          </Grid>
        </div>
      )}
    </>
  ) : (
    <div>
      <LoadingSpinner />
    </div>
  );
}
